import {
  CombinedKameleonBlockTypes,
  isKameleonElement,
  KameleonBlockType,
  KameleonElement,
  PrimaryCollectionBlockType,
} from "@kameleon-core/types";

/**
 * This function merges the `layoutProps` key with all the children of the `PrimaryBlocks`,
 * so that each primaryBlock child gets the same layoutProps.
 *
 * This is only applicable for PrimaryCollectionBlocks, since this block type renders a collection of PrimaryBlocks,
 * of which each given layoutProps should be passed to the PrimaryBlock children as well.
 *
 * E.g:
 *
 * {
 *     layoutProps: {
 *         prop1: true
 *     },
 *     PrimaryBlocks: [
 *         {
 *             someProp:true
 *         },
 *         {
 *             someProp:false
 *         }
 *     ]}
 * }
 *
 * Will become:
 *
 * {
 *     layoutProps: {
 *         prop1: true
 *     },
 *     PrimaryBlocks: [
 *         {
 *             someProp:true
 *             layoutProps: {
 *                 prop1:true
 *             }
 *         },
 *               {
 *             someProp:false
 *             layoutProps: {
 *                 prop1:true
 *             }
 *         }
 *     ]}
 * }
 * @param props
 */
const mergeLayoutPropsInDefaults = <T>(props: T): T => {
  const defaults: any = { ...props };
  if (defaults.contentProps) {
    return {
      ...defaults,
      contentProps: {
        PrimaryBlocks: defaults?.contentProps?.PrimaryBlocks?.map((p: any) => ({
          ...p,
          layoutProps: { ...defaults.layoutProps },
        })),
      },
    };
  }
  return defaults;
};

/**
 * Make sure that the defaultsFile is built like:
 * {
 *      "contentProps": {},
 *      "layoutProps: {}
 * }
 *
 * Then, we modify its type to be:
 * {
 *     contentProps: {
 *         layoutProps: {}
 *         ...{}
 *     }
 * }
 *
 * However, we need to enforce that even optional fields within type T are required (except those base fields),
 * so that you MUST include them within your defaults.json
 */
type BlockType = KameleonBlockType<{}>;
type IgnoredFromDefaults = Pick<
  CombinedKameleonBlockTypes<{}>,
  | "layoutProps"
  | "PrimaryBlocks"
  | "SecondaryBlock"
  | "TertiaryBlock"
  | "FooterWidgetBlocks"
  | "NavWidgetBlocks"
>;

// We do this by first pick all properties having a certain type
type PickByType<T, Value> = {
  [P in keyof T as T[P] extends Value | undefined ? P : never]: T[P];
};
// We reverse the above process by omitting those from the given type
type OmitByType<T, TypeToOmit> = Omit<T, keyof PickByType<T, TypeToOmit>>;

// Define some omit properties for exception properties we want to exclude to be required.
// Those include: functions (cant be defined in default.json) and
//   - props with keys starting with _$key. Those are internal props.
//   - props with name: "children", those are react children and do not have defaults
type OmitPropsByFilterKey<T> = Omit<
  T,
  `_${string}` | `subMenu` | "children" | keyof IgnoredFromDefaults
>; // Add more | conditions to filter those as well
type OmitFilterKeysAndFunctionProps<T> = OmitPropsByFilterKey<
  OmitByType<T, (...args: any) => any>
>;

// Next we make every property required (nested as well), except those that we defined in the above omits.
type DeepRequiredWithExceptions<T, RT extends T = Required<T>> = {
  [K in keyof OmitFilterKeysAndFunctionProps<RT>]-?: RT[K] extends object
    ? DeepRequiredWithExceptions<OmitFilterKeysAndFunctionProps<RT[K]>>
    : RT[K];
};

type DefaultFile<T extends BlockType> = (T extends PrimaryCollectionBlockType
  ? {
      contentProps: Omit<
        DeepRequiredWithExceptions<T>,
        keyof IgnoredFromDefaults
      > & { PrimaryBlocks: any[] };
    }
  : {
      contentProps: Omit<
        DeepRequiredWithExceptions<T>,
        keyof IgnoredFromDefaults
      >;
    }) &
  Pick<T, "layoutProps">;

export const getDefaultProps = <
  R = [Error, "Typed parameter of type KameleonBlockType is required"],
  T extends R & BlockType = R & BlockType
>(
  elementOrDefaultJson: KameleonElement | DefaultFile<T>
): T => {
  if (isKameleonElement(elementOrDefaultJson)) {
    const element = { ...elementOrDefaultJson };
    const layoutProps: any = element?.defaultProps?.layoutProps ?? {};
    const contentProps: any = element?.defaultProps?.contentProps ?? {};
    return {
      layoutProps: { ...layoutProps },
      ...contentProps,
    };
  }

  const defaultJson = { ...(elementOrDefaultJson as any) };
  return mergeLayoutPropsInDefaults({
    layoutProps: { ...defaultJson?.layoutProps },
    ...{ ...defaultJson?.contentProps },
  });
};
